import abcoLogo from "assets/abco-logo.png";
import AddToCart from "components/item/AddToCart";
import ItemStatusBadge from "components/item/ItemStatusBadge";
import { Box, Card, HStack, Icon, Image, Link, VStack } from "components/ui";
import ItemBadge from "components/ui/ItemBadge";
import { Typography } from "components/ui/Typography";
import { Clock } from "lucide-react-native";
import React, { useState } from "react";
import { Platform, useWindowDimensions, ViewStyle } from "react-native";
import { useIsMobile } from "utils/hooks/useIsMobile";
import { useSiteSearch } from "utils/hooks/useSiteSearch";
import Animated, { Easing, useAnimatedStyle, useSharedValue, withRepeat, withTiming, interpolate } from "react-native-reanimated";
import { LinearGradient } from "expo-linear-gradient";
import { useToken } from "@gluestack-style/react";
import CardTag from "../CardTag";
import { PageRead } from "store/abcoApi";

const articleTypeMap: { [key: number]: string } = {
	1: "News",
	2: "Guide",
	3: "Case Study",
	4: "Innovation",
};

type Props = {
	data: PageRead;
};

const ImageSkeleton = () => {
	const shimmerValue = useSharedValue(0);
	const pulseValue = useSharedValue(0);

	React.useEffect(() => {
		// Use yo-yo effect by setting reverse to true
		shimmerValue.value = withRepeat(
			withTiming(1, { duration: 1800, easing: Easing.inOut(Easing.ease) }),
			-1, // infinite repeats
			true, // yo-yo effect (reverses direction)
		);

		pulseValue.value = withRepeat(
			withTiming(1, { duration: 1200, easing: Easing.inOut(Easing.ease) }),
			-1,
			true, // yo-yo effect
		);
	}, []);

	const gradientStyle = useAnimatedStyle(() => {
		const translateX = interpolate(shimmerValue.value, [0, 1], [-300, 300]);

		return {
			position: "absolute",
			top: 0,
			bottom: 0,
			left: 0,
			right: 0,
			transform: [{ translateX }],
		};
	});

	const pulseStyle = useAnimatedStyle(() => {
		return {
			position: "absolute",
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			backgroundColor: "#e0e0e0",
			opacity: 0.3 + pulseValue.value * 0.4, // Subtle but visible pulsing
			borderRadius: 20,
		};
	});

	const containerStyle: ViewStyle = {
		position: "absolute",
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: "#e8e8e8", // Slightly lighter base color
		borderRadius: 20,
		overflow: "hidden",
	};

	return (
		<Box style={containerStyle}>
			<Animated.View style={pulseStyle} />
			<Animated.View style={gradientStyle}>
				<LinearGradient
					colors={["rgba(255,255,255,0)", "rgba(255,255,255,0.8)", "rgba(255,255,255,0)"]}
					start={{ x: 0, y: 0 }}
					end={{ x: 1, y: 0 }}
					style={{
						width: 300,
						height: "100%",
					}}
				/>
			</Animated.View>
		</Box>
	);
};

const ArticleCard = React.memo(({ data }: Props) => {
	const isMobile = useIsMobile();
	const { width } = useWindowDimensions();
	const { close } = useSiteSearch();
	const [imageLoaded, setImageLoaded] = useState(false);
	const [isHovered, setIsHovered] = useState(false);
	const placeholderReadTime = 5;
	const placeholderImage = data.featureImageUrl;
	const tag = articleTypeMap[data.articleType as number] || "";
	let resolvedNeutral500 = useToken("colors", "primarylight");
	if (isMobile) {
		return (
			<Card maxWidth={width - 32} flex={1} w={"$full"} p={16} overflow="hidden">
				<Link href={data.path || "#"} onPress={() => data.path && close()}>
					<VStack space="sm" flex={1}>
						<Box bg="white" borderRadius="$lg" overflow="hidden" w="$full" h={180} position="relative">
							{!imageLoaded && <ImageSkeleton />}
							{placeholderImage ? (
								<Image
									aspectRatio={16 / 9}
									w="$full"
									source={process.env.EXPO_PUBLIC_ABCO_API_CDNURL + placeholderImage + "?width=400&quality=75&format=webp"}
									borderTopLeftRadius={8}
									borderTopRightRadius={8}
									style={{
										width: "100%",
										height: "100%",
										objectFit: "cover",
									}}
									alt={data.pageName || "Article image"}
									resizeMode="cover"
									onLoad={() => setImageLoaded(true)}
								/>
							) : (
								<Image
									aspectRatio={16 / 9}
									w="$full"
									source={abcoLogo}
									borderTopLeftRadius={8}
									borderTopRightRadius={8}
									alignSelf="center"
									// @ts-ignore
									style={{
										width: "60%",
										height: "100%",
										objectFit: "contain",
										opacity: 0.3,
										filter: "grayscale(100%)",
									}}
									alt={data.pageName || "Placeholder article image"}
									resizeMode="contain"
									onLoad={() => setImageLoaded(true)}
								/>
							)}
						</Box>

						<VStack gap={8} p="$3" flex={1}>
							<HStack space="xs" flexWrap="wrap">
								<CardTag tag={tag} />
							</HStack>
							<Typography.H4 weight="bold" numberOfLines={2} ellipsizeMode="tail">
								{data.pageName}
							</Typography.H4>

							<HStack space="xs" alignItems="center" mt="auto">
								<Clock size={14} color={resolvedNeutral500} />
								<Typography.ParagraphSmall color="$neutral500">{placeholderReadTime} min read time</Typography.ParagraphSmall>
							</HStack>
						</VStack>
					</VStack>
				</Link>
			</Card>
		);
	}

	return (
		<Card p={16} overflow="hidden">
			<Link
				onHoverIn={() => {
					setIsHovered(true);
				}}
				onHoverOut={() => {
					setIsHovered(false);
				}}
				href={`${data.path}`}
			>
				<VStack space="md" flex={1}>
					<Box bg="white" borderRadius="$2xl" overflow="hidden" w="$full" h={300} position="relative">
						{!imageLoaded && <ImageSkeleton />}
						{placeholderImage ? (
							<Image
								source={process.env.EXPO_PUBLIC_ABCO_API_CDNURL + placeholderImage + "?width=600&quality=75&format=webp"}
								style={{
									width: "100%",
									height: 360,
									objectFit: "cover",
									transform: isHovered ? "scale(1.05)" : "scale(1)",
									// @ts-ignore
									transition: "transform 0.3s ease",
								}}
								alt={data.pageName || "Article image"}
							/>
						) : (
							// <Image
							// 	aspectRatio={1}
							// 	w="$full"
							// 	source={process.env.EXPO_PUBLIC_ABCO_API_CDNURL + placeholderImage + "?width=600&quality=75&format=webp"}
							// 	borderTopLeftRadius={isMobile ? 8 : 20}
							// 	borderTopRightRadius={isMobile ? 8 : 20}
							// 	style={{
							// 		width: "100%",
							// 		height: "100%",
							// 		maxHeight: 280,
							// 		objectFit: "cover",
							// 		transform: isHovered ? "scale(1.05)" : "scale(1)",
							// 		// @ts-ignore
							// 		transition: "transform 0.3s ease",
							// 	}}
							// 	alt={data.pageName || "Article image"}
							// 	resizeMode="contain"
							// 	onLoad={() => setImageLoaded(true)}
							// />
							<Image
								aspectRatio={1}
								w="$full"
								source={abcoLogo}
								borderTopLeftRadius={isMobile ? 8 : 20}
								borderTopRightRadius={isMobile ? 8 : 20}
								alignSelf="center"
								// @ts-ignore
								style={{
									width: "80%",
									height: "100%",
									// maxHeight: 280,
									objectFit: "cover",
									// minHeight: 280,
									opacity: 0.3,
									filter: "grayscale(100%)",
									transform: isHovered ? "scale(1.05)" : "scale(1)",
									transition: "transform 0.3s ease",
								}}
								alt={data.pageName || "Placeholder article image"}
								resizeMode="contain"
								onLoad={() => setImageLoaded(true)}
							/>
						)}
					</Box>

					<VStack gap={12} pb="$4" mt={12} flex={1}>
						<HStack space="sm" flexWrap="wrap">
							<CardTag tag={tag} />
						</HStack>
						{Platform.OS === "web" ? (
							<h3 className="item-list-base-title" style={{ color: isHovered ? "#1a428a" : "#173648", textDecorationColor: isHovered ? "#1a428a" : "transparent" }}>
								{data.pageName}
							</h3>
						) : (
							<Typography.H4 weight="bold" numberOfLines={2} ellipsizeMode="tail">
								{data.pageName}
							</Typography.H4>
						)}
						{/* <Typography.H3
							underline={isHovered}
							weight="bold"
							// @ts-ignore - web only prop
							style={{ transition: "all 0.3s ease", lineHeight: 36 }}
						>
							{data.pageName}
						</Typography.H3> */}

						<HStack space="sm" alignItems="center" mt="auto">
							<Clock size={16} color={resolvedNeutral500} />
							<Typography.ParagraphSmall color="$neutral500">{placeholderReadTime} min read time</Typography.ParagraphSmall>
						</HStack>
					</VStack>
				</VStack>
			</Link>
		</Card>
	);
});

export default ArticleCard;
